import { listProjects, createProject, updateProject, deleteProject, getProject } from '../api/projects.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { getVisibleLayerIds, applyVisibleLayerIds } from './myLayersPanel.js';
import { openAuthModal } from '../auth/modal.js';
import { openProjectShareModal } from './projectShareModal.js';

let activeProjectId = null;

export function setActiveProjectId(id) { activeProjectId = id; syncProjectInUrl(); }
export function getActiveProjectId() { return activeProjectId; }

/**
 * Reflect the current project id in the URL (?project=N).
 * Pure cosmetic — uses replaceState so it doesn't pollute history.
 */
function syncProjectInUrl() {
  const url = new URL(location.href);
  if (activeProjectId) url.searchParams.set('project', String(activeProjectId));
  else                 url.searchParams.delete('project');
  history.replaceState(null, '', url);
}

/**
 * If the page was loaded with ?project=N, open that project automatically.
 * Called once at app start by main.js, after auth+map are ready.
 */
export async function maybeOpenProjectFromUrl(map, switchBasemapFn) {
  if (!auth.isAuthenticated()) return;
  const id = +(new URLSearchParams(location.search).get('project'));
  if (!id) return;
  try {
    const project = await getProject(id);
    await applyState(map, project.state, switchBasemapFn);
    activeProjectId = project.id;
    document.querySelector('.project-name').textContent = project.name;
    syncProjectInUrl();
    toast(`Opened "${project.name}"`);
  } catch {
    // Stale link or no access — silently drop the param
    const url = new URL(location.href);
    url.searchParams.delete('project');
    history.replaceState(null, '', url);
  }
}

export function captureMapState(map, basemap) {
  const c = map.getCenter();
  return {
    basemap,
    center: [c.lng, c.lat],
    zoom: map.getZoom(),
    visible_user_layers: getVisibleLayerIds()
  };
}

/**
 * Restore a project's saved map state. Each step is independently
 * try/catch'd — a missing layer or stale basemap shouldn't sink the
 * whole restore.
 */
async function applyState(map, state, switchBasemapFn) {
  if (!state) return;

  // Camera
  try {
    if (Array.isArray(state.center) && typeof state.zoom === 'number') {
      map.flyTo({ center: state.center, zoom: state.zoom, duration: 600 });
    }
  } catch (e) {
    console.warn('Project restore: camera failed', e);
  }

  // Basemap
  try {
    if (state.basemap) {
      const btn = document.querySelector(`[data-basemap="${state.basemap}"]`);
      if (btn) switchBasemapFn(map, state.basemap, btn);
    }
  } catch (e) {
    console.warn('Project restore: basemap failed', e);
  }

  // Visible user layers — applyVisibleLayerIds already swallows per-layer
  // errors, but wrap it in case the underlying API call throws.
  try {
    if (Array.isArray(state.visible_user_layers)) {
      await applyVisibleLayerIds(state.visible_user_layers);
    }
  } catch (e) {
    console.warn('Project restore: visible layers failed', e);
  }
}

export async function saveCurrentAsProject(map, basemap) {
  if (!auth.isAuthenticated()) { openAuthModal(); return; }
  const state = captureMapState(map, basemap);

  if (activeProjectId) {
    try {
      await updateProject(activeProjectId, { state });
      toast('Project saved');
      return;
    } catch (e) {
      toast(`Save failed: ${e.message}`);
      return;
    }
  }

  const name = prompt('Project name:', 'Untitled project');
  if (!name) return;
  try {
    const project = await createProject({ name, state });
    activeProjectId = project.id;
    document.querySelector('.project-name').textContent = project.name;
    syncProjectInUrl();
    toast(`Created "${project.name}"`);
  } catch (e) {
    toast(`Save failed: ${e.message}`);
  }
}

export async function openProjectsMenu(map, switchBasemapFn) {
  if (!auth.isAuthenticated()) { openAuthModal(); return; }

  let projects;
  try { projects = await listProjects(); }
  catch (e) { toast(e.message); return; }

  if (projects.length === 0) {
    toast('No saved projects yet. Use "Save" to create your first.');
    return;
  }

  // Owned vs shared-with-me — render under separate headings.
  const owned  = projects.filter(p => p.role === 'owner');
  const shared = projects.filter(p => p.role !== 'owner');

  const html = `
    <div class="modal-bg show" id="projectsModal">
      <div class="modal" style="width:480px">
        <h3>Open project</h3>
        <div style="max-height:380px;overflow-y:auto;margin-top:10px;display:flex;flex-direction:column;gap:14px">
          ${owned.length ? `
            <div>
              <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:0.14em;color:var(--text3);margin-bottom:6px;font-weight:600">Mine</div>
              <div style="display:flex;flex-direction:column;gap:6px">
                ${owned.map(renderRow).join('')}
              </div>
            </div>
          ` : ''}
          ${shared.length ? `
            <div>
              <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:0.14em;color:var(--text3);margin-bottom:6px;font-weight:600">Shared with me</div>
              <div style="display:flex;flex-direction:column;gap:6px">
                ${shared.map(renderRow).join('')}
              </div>
            </div>
          ` : ''}
        </div>
        <div style="text-align:right;margin-top:14px">
          <button class="btn" data-action="closeProjectsModal">Close</button>
        </div>
      </div>
    </div>
  `;

  // Mount
  document.querySelector('#projectsModal')?.remove();
  document.body.insertAdjacentHTML('beforeend', html);

  const root = document.getElementById('projectsModal');
  root.addEventListener('click', async ev => {
    const open = ev.target.closest('[data-open-project]');
    if (open) {
      const id = +open.dataset.openProject;
      try {
        const project = await getProject(id);
        await applyState(map, project.state, switchBasemapFn);
        activeProjectId = project.id;
        document.querySelector('.project-name').textContent = project.name;
        syncProjectInUrl();
        toast(`Opened "${project.name}"`);
        root.remove();
      } catch (e) { toast(`Open failed: ${e.message}`); }
      return;
    }
    const share = ev.target.closest('[data-share-project]');
    if (share) {
      const id = +share.dataset.shareProject;
      const project = projects.find(p => p.id === id);
      if (project) await openProjectShareModal(project);
      return;
    }
    const del = ev.target.closest('[data-delete-project]');
    if (del) {
      if (!confirm('Delete this project? This cannot be undone.')) return;
      const id = +del.dataset.deleteProject;
      try {
        await deleteProject(id);
        if (activeProjectId === id) {
          activeProjectId = null;
          document.querySelector('.project-name').textContent = 'Untitled project';
          syncProjectInUrl();
        }
        toast('Project deleted');
        root.remove();
      } catch (e) { toast(`Delete failed: ${e.message}`); }
    }
  });
}

function renderRow(p) {
  const isOwner = p.role === 'owner';
  const roleBadge = isOwner ? '' : `
    <span style="display:inline-block;padding:1px 7px;border-radius:999px;background:var(--bg4);color:var(--text2);font-size:9.5px;letter-spacing:0.06em;text-transform:uppercase;font-family:var(--mono);margin-left:6px">${p.role}</span>
  `;
  const ownerLine = isOwner
    ? `Updated ${new Date(p.updated_at).toLocaleString()}`
    : `by ${escape(p.owner_full_name || p.owner_email || 'someone')} · updated ${new Date(p.updated_at).toLocaleString()}`;

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border:1px solid var(--border);border-radius:6px;background:var(--bg3)">
      <div style="min-width:0;flex:1">
        <div style="font-size:13px;font-weight:600;display:flex;align-items:center">${escape(p.name)}${roleBadge}</div>
        <div style="font-size:10.5px;color:var(--text3);font-family:var(--mono)">${ownerLine}</div>
      </div>
      <div style="display:flex;gap:6px">
        <button class="btn-sm btn-run" data-open-project="${p.id}">Open</button>
        ${isOwner ? `
          <button class="btn-sm btn-outline" data-share-project="${p.id}" title="Share">↗</button>
          <button class="btn-sm btn-outline" data-delete-project="${p.id}" title="Delete">✕</button>
        ` : ''}
      </div>
    </div>
  `;
}

export function closeProjectsModal() {
  document.getElementById('projectsModal')?.remove();
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
